// ===========================================================================
// backendWarmup.js — Art. 325 da Constituição
// Warm-up + Retry para backend Render (cold start ~30-60s)
// Uso: import { fetchWithWarmup } from '../utils/backendWarmup';
// ===========================================================================

const BACKEND_URL = String(
  import.meta?.env?.VITE_BACKEND_URL || 'https://rda-backend-thuq.onrender.com'
).trim().replace(/\/+$/, '');

const HEALTH_ENDPOINT = `${BACKEND_URL}/health`;
const WARMUP_CACHE_KEY = 'rda_backend_warm';
const WARMUP_TTL_MS = 10 * 60 * 1000; // 10 minutos

// ── Verifica se o backend já foi "acordado" recentemente ──────────────────
function isBackendWarm() {
  try {
    const ts = Number(sessionStorage.getItem(WARMUP_CACHE_KEY) || 0);
    return ts > 0 && (Date.now() - ts) < WARMUP_TTL_MS;
  } catch {
    return false;
  }
}

function markBackendWarm() {
  try {
    sessionStorage.setItem(WARMUP_CACHE_KEY, String(Date.now()));
  } catch { /* ignore */ }
}

// ── Ping leve no /health para acordar o backend ───────────────────────────
export async function warmUpBackend({ timeoutMs = 50000, onWaking } = {}) {
  if (isBackendWarm()) return true;

  if (typeof onWaking === 'function') onWaking();

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const resp = await fetch(HEALTH_ENDPOINT, {
      method: 'GET',
      signal: controller.signal,
      cache: 'no-store',
    });
    clearTimeout(timer);
    if (resp.ok) {
      markBackendWarm();
      return true;
    }
    return false;
  } catch (err) {
    clearTimeout(timer);
    console.warn('[backendWarmup] Health check falhou:', err?.message);
    return false;
  }
}

// ── Fetch com warm-up automático + retry ──────────────────────────────────
export async function fetchWithWarmup(url, options = {}, {
  maxRetries = 2,
  retryDelayMs = 3000,
  warmupTimeoutMs = 50000,
  onWaking,
  onRetry,
} = {}) {
  // Passo 1: Garantir que o backend está acordado
  const warm = await warmUpBackend({ timeoutMs: warmupTimeoutMs, onWaking });
  if (!warm) {
    console.warn('[fetchWithWarmup] Backend não respondeu ao warm-up. Tentando requisição mesmo assim...');
  }

  // Passo 2: Fazer a requisição com retry
  let lastError = null;
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const resp = await fetch(url, options);
      if (resp.ok) {
        markBackendWarm();
        return resp;
      }
      // Se resposta não-ok mas não é timeout, retornar para o caller tratar
      if (resp.status < 500) return resp;
      // 5xx → retry
      lastError = new Error(`Backend retornou ${resp.status}: ${resp.statusText}`);
    } catch (err) {
      lastError = err;
    }

    if (attempt < maxRetries) {
      if (typeof onRetry === 'function') onRetry(attempt, maxRetries);
      await new Promise((r) => setTimeout(r, retryDelayMs));
    }
  }

  // Todas tentativas falharam
  throw lastError || new Error('Backend indisponível após múltiplas tentativas.');
}

export default fetchWithWarmup;
